import React, { useCallback, useMemo } from "react";
import { Column, ColumnDef, PaginationState, Row } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import { useTranslation } from "react-i18next";
import { NameAndAvatar } from "./NameAndAvatar";
import { StudentListEmptyState } from "./StudentListEmptyState";
import { StudentInterestBadge } from "./StudentInterestBadge";
import { MoreVertical } from "lucide-react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { Student } from "@/types/Student";
import { Sort } from "@/components/DataTable/Header/THeaderCellSorting";
import {
  SortDirectionEnum,
  StudentListOrganizationSortableFieldEnum,
  StudentListMostEngagedSortableFieldEnum,
  FunnelStateEnum,
  PartnerFeatureEnum,
} from "@/__generated__/types";
import type { SortableFieldEnum } from "@/types/StudentOrdering";
import { StudentOrdering } from "@/types/StudentOrdering";
import { FUNNEL_STATE_TRANSLATION_KEYS } from "@/util/funnelGroups";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { serializeQualifiedChatId } from "@zeemee/react-web-chat";
import { useMultiSidebar } from "@/components/ui/MultiSidebar";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

// Map of column ID to the Enum field used for sorting in graphql
const ID_TO_SORT_ENUM: Record<string, SortableFieldEnum> = {
  name: StudentListOrganizationSortableFieldEnum.Name,
  funnelState: StudentListOrganizationSortableFieldEnum.FunnelState,
  studentType: StudentListOrganizationSortableFieldEnum.StudentType,
  phoneNumber: StudentListOrganizationSortableFieldEnum.Phone,
  index: StudentListOrganizationSortableFieldEnum.CommitIndex,
  location: StudentListOrganizationSortableFieldEnum.Location,
  lastSignInAt: StudentListOrganizationSortableFieldEnum.LastActive,
  messages: StudentListMostEngagedSortableFieldEnum.MessagesSent,
  riseApplyIndex: StudentListOrganizationSortableFieldEnum.RiseApplyIndex,
  riseCommitIndex: StudentListOrganizationSortableFieldEnum.RiseCommitIndex,
  riseYieldIndex: StudentListOrganizationSortableFieldEnum.RiseYieldIndex,
  riseMeltIndex: StudentListOrganizationSortableFieldEnum.RiseMeltIndex,
};

// Map of sorting enum to column ID
const SORT_ENUM_TO_ID = Object.entries(ID_TO_SORT_ENUM).reduce(
  (acc, curr) => {
    const [id, enumVal] = curr;
    acc[enumVal] = id;
    return acc;
  },
  {} as Record<SortableFieldEnum, keyof typeof ID_TO_SORT_ENUM>,
);

interface StudentListGridProps {
  students: Student[];
  showMessages?: boolean;
  isLoading?: boolean;
  page: number; // 1-based page index
  onPageChange?: (page: number) => void;
  totalPages: number;
  perPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  ordering?: StudentOrdering;
  setOrdering?: (ordering: StudentOrdering) => void;
  isError?: boolean;
  hasActiveFilters?: boolean;
}

export const StudentsListSGrid = ({
  students,
  showMessages = false,
  isLoading = false,
  page,
  onPageChange,
  totalPages,
  perPage,
  hasNextPage,
  hasPreviousPage,
  ordering,
  setOrdering,
  isError = false,
  hasActiveFilters = false,
}: StudentListGridProps) => {
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();
  const { setOpen } = useMultiSidebar("filter");
  const { hasOrgFeature } = useOrgPermissions();

  const hasRise =
    hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels) ?? false;

  const paginationState = useMemo<PaginationState>(
    () => ({
      pageIndex: page - 1,
      pageSize: perPage,
    }),
    [page, perPage],
  );

  const handlePaginationChange = useCallback<
    React.Dispatch<React.SetStateAction<PaginationState>>
  >(
    (updater) => {
      const currentState: PaginationState = {
        pageIndex: page - 1,
        pageSize: perPage,
      };

      const nextState =
        typeof updater === "function"
          ? (updater as (old: PaginationState) => PaginationState)(currentState)
          : updater;

      const nextPage = nextState.pageIndex + 1;
      if (nextPage !== page && onPageChange) {
        onPageChange(nextPage);
      }
    },
    [onPageChange, page, perPage],
  );

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewProfile = useCallback(
    (studentId: string) => {
      navToRoute("student", { studentId }, {}, { newTab: true });
      setOpen(false);
    },
    [navToRoute, setOpen],
  );

  const handleMessage = useCallback(
    (studentUuid: string) => {
      navToRoute("selectedChat", {
        chat:
          serializeQualifiedChatId({
            chatType: "chat",
            chatId: studentUuid,
          }) || "",
      });
    },
    [navToRoute],
  );

  const onSort = useCallback(
    (sort: Sort) => {
      const field = ID_TO_SORT_ENUM[sort.orderingField];
      if (field && setOrdering && ordering) {
        setOrdering(
          ordering.clone({ field, direction: sort.orderingDirection }),
        );
      }
    },
    [setOrdering, ordering],
  );

  const renderHeader = useCallback(
    (column: Column<Student>, translationKey: string) => {
      const canSort = !!setOrdering && !!ID_TO_SORT_ENUM[column.id];

      return canSort ? (
        <DataTable.THeaderCellSorting column={column} onSortingChange={onSort}>
          {t(translationKey)}
        </DataTable.THeaderCellSorting>
      ) : (
        <DataTable.THeaderCell>{t(translationKey)}</DataTable.THeaderCell>
      );
    },
    [onSort, t, setOrdering],
  );

  const initialSortState =
    ordering &&
    ordering.field &&
    ordering.direction &&
    SORT_ENUM_TO_ID[ordering.field]
      ? [
          {
            id: SORT_ENUM_TO_ID[ordering.field],
            desc: ordering.direction === SortDirectionEnum.Desc,
          },
        ]
      : undefined;

  const renderIndexCell = useCallback(
    (indexValue?: string | null) => {
      return (
        <DataTable.TBodyCell>
          {indexValue ? t(`filters.fieldOptions.${indexValue}`) : "–"}
        </DataTable.TBodyCell>
      );
    },
    [t],
  );

  const columns: ColumnDef<Student>[] = [
    {
      id: "name",
      accessorFn: (row) => row.studentOrganizationStatus?.name,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "name",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.name"),
      cell: ({ row }: { row: Row<Student> }) => {
        return (
          <DataTable.TBodyCell>
            <NameAndAvatar
              name={row.original.studentOrganizationStatus?.name}
              imageUrl={row.original.imageUrl}
              avatarShape="square"
              avatarSize="size-12"
            />
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "funnelState",
      accessorKey: "funnelState",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "funnelState",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.funnelState"),
      cell: ({ row }: { row: Row<Student> }) => {
        const funnelState = row.original.studentOrganizationStatus?.funnelState;
        const translationKey = funnelState
          ? FUNNEL_STATE_TRANSLATION_KEYS[funnelState as FunnelStateEnum]
          : null;
        const translatedFunnelState = translationKey
          ? t(`global.funnelStates.${translationKey}`)
          : funnelState;

        return (
          <DataTable.TBodyCell>
            {translatedFunnelState || ""}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "cohortYear",
      accessorKey: "cohortYear",
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: "cohortYear",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.cohortYear"),
      cell: ({ row }: { row: Row<Student> }) => {
        return (
          <DataTable.TBodyCell>
            {row.original.studentOrganizationStatus?.cohortYear || ""}
          </DataTable.TBodyCell>
        );
      },
    },
    ...(showMessages
      ? [
          {
            id: "messages",
            accessorKey: "messages",
            enableSorting: true,
            enableColumnFilter: true,
            meta: { label: "messages" },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.messages"),
            cell: ({ row }: { row: Row<Student> }) => (
              <DataTable.TBodyCell>
                {row.original.studentOrganizationStatus?.messagesSent ?? 0}
              </DataTable.TBodyCell>
            ),
          },
        ]
      : []),
    ...(hasRise
      ? [
          {
            id: "riseApplyIndex",
            accessorKey: "riseApplyIndex",
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
              label: "riseApplyIndex",
            },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.applyIndex"),
            cell: ({ row }: { row: Row<Student> }) => {
              return renderIndexCell(
                row.original.studentOrganizationStatus?.rise?.applyIndex,
              );
            },
          },
          {
            id: "riseCommitIndex",
            accessorKey: "riseCommitIndex",
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
              label: "riseCommitIndex",
            },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.commitIndex"),
            cell: ({ row }: { row: Row<Student> }) => {
              return renderIndexCell(
                row.original.studentOrganizationStatus?.rise?.commitIndex,
              );
            },
          },
          {
            id: "riseYieldIndex",
            accessorKey: "riseYieldIndex",
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
              label: "riseYieldIndex",
            },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.yieldIndex"),
            cell: ({ row }: { row: Row<Student> }) => {
              return renderIndexCell(
                row.original.studentOrganizationStatus?.rise?.yieldIndex,
              );
            },
          },
          {
            id: "riseMeltIndex",
            accessorKey: "riseMeltIndex",
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
              label: "riseMeltIndex",
            },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.meltIndex"),
            cell: ({ row }: { row: Row<Student> }) => {
              return renderIndexCell(
                row.original.studentOrganizationStatus?.rise?.meltIndex,
              );
            },
          },
        ]
      : [
          {
            id: "index",
            accessorKey: "index",
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
              label: "index",
            },
            header: ({ column }: { column: Column<Student> }) =>
              renderHeader(column, "studentCard.gridView.commitIndex"),
            cell: ({ row }: { row: Row<Student> }) => {
              const commitIndex =
                row.original.studentOrganizationStatus?.commitIndex;
              return (
                <DataTable.TBodyCell>
                  {commitIndex ? t(`filters.fieldOptions.${commitIndex}`) : ""}
                </DataTable.TBodyCell>
              );
            },
          },
        ]),
    {
      id: "interests",
      accessorKey: "interests",
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: "interests",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.interests"),
      cell: ({ row }: { row: Row<Student> }) => {
        return (
          <DataTable.TBodyCell>
            <StudentInterestBadge
              interests={row.original.interests ?? []}
              maxInterests={3}
              className="w-full flex-row flex-nowrap"
            />
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "major",
      accessorKey: "major",
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: "major",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.major"),
      cell: ({ row }: { row: Row<Student> }) => {
        const majors = row.original.majors ?? [];
        const firstMajor = majors[0];
        const remainingCount = majors.length - 1;

        return (
          <DataTable.TBodyCell>
            {firstMajor ? (
              <div className="flex items-center gap-1">
                <span className="truncate">{firstMajor.name}</span>
                {remainingCount > 0 && (
                  <span className="flex-shrink-0 text-muted-foreground">
                    +{remainingCount}
                  </span>
                )}
              </div>
            ) : (
              ""
            )}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "location",
      accessorKey: "location",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "location",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.location"),
      cell: ({ row }: { row: Row<Student> }) => {
        return (
          <DataTable.TBodyCell>
            {row.original.studentOrganizationStatus?.location}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "goingStatus",
      accessorKey: "goingStatus",
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: "goingStatus",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.goingStatus"),
      cell: ({ row }: { row: Row<Student> }) => {
        const goingStatus = row.original.studentOrganizationStatus?.goingStatus;
        return (
          <DataTable.TBodyCell>
            {goingStatus ? t(`filters.fieldOptions.${goingStatus}`) : ""}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "studentType",
      accessorKey: "studentType",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "studentType",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.studentType"),
      cell: ({ row }: { row: Row<Student> }) => {
        const studentType = row.original.studentOrganizationStatus?.studentType;
        return (
          <DataTable.TBodyCell>
            {studentType ? t(`filters.fieldOptions.${studentType}`) : ""}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "assignedCounselor",
      accessorKey: "assignedCounselor",
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: "assignedCounselor",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.assignedCounselor"),
      cell: ({ row }: { row: Row<Student> }) => {
        const counselor =
          row.original.studentOrganizationStatus?.assignedCounselor;
        const counselorName = counselor ? counselor.name?.trim() : "";
        return <DataTable.TBodyCell>{counselorName}</DataTable.TBodyCell>;
      },
    },
    {
      id: "lastSignInAt",
      accessorKey: "lastSignInAt",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "lastSignInAt",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.lastSignInAt"),
      cell: ({ row }: { row: Row<Student> }) => {
        return (
          <DataTable.TBodyCell>
            {row.original.studentOrganizationStatus?.lastActiveAt &&
              formatDate(row.original.studentOrganizationStatus?.lastActiveAt)}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "phoneNumber",
      accessorKey: "phoneNumber",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "phoneNumber",
      },
      header: ({ column }: { column: Column<Student> }) =>
        renderHeader(column, "studentCard.gridView.phone"),
      cell: ({ row }: { row: Row<Student> }) => {
        try {
          const phoneNumber = row.original.studentOrganizationStatus
            ?.phoneNumber
            ? parsePhoneNumberWithError(
                String(row.original.studentOrganizationStatus?.phoneNumber),
              )
            : null;
          return (
            <DataTable.TBodyCell>
              {phoneNumber
                ? `+${phoneNumber.countryCallingCode} ${phoneNumber.formatNational()}`
                : ""}
            </DataTable.TBodyCell>
          );
        } catch {
          return <DataTable.TBodyCell>{""}</DataTable.TBodyCell>;
        }
      },
    },
    {
      id: "actions",
      accessorKey: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        label: "actions",
      },
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<Student> }) => {
        const student = row.original;
        return (
          <DataTable.TBodyCell>
            <Menubar className="h-8 cursor-pointer p-0 hover:bg-muted">
              <MenubarMenu>
                <MenubarTrigger
                  className="h-8 w-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" aria-hidden="true" />
                </MenubarTrigger>
                <MenubarContent align="end">
                  <MenubarItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewProfile(student.id || "");
                    }}
                  >
                    {t("global.actions.viewProfile")}
                  </MenubarItem>
                  <MenubarItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessage(student.uuid || "");
                    }}
                  >
                    {t("global.actions.message")}
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return (
    <div className="h-full w-full rounded-md text-sm text-muted-foreground">
      <div className="w-full">
        <DataTable
          tableHeader={<DataTable.THeader />}
          tableBody={
            <DataTable.TBody
              onRowClick={(e) =>
                handleViewProfile((e.original as Student).id || "")
              }
            />
          }
          pagination={
            <>
              <DataTable.PaginationLabel pageCount={totalPages} />
              <DataTable.PaginationControls
                onNextPage={
                  hasNextPage && onPageChange && (() => onPageChange(page + 1))
                }
                onPreviousPage={
                  hasPreviousPage &&
                  onPageChange &&
                  (() => onPageChange(page - 1))
                }
              />
            </>
          }
          isLoading={isLoading}
          emptyState={
            <StudentListEmptyState
              isError={isError}
              hasActiveFilters={hasActiveFilters}
            />
          }
          columns={columns}
          data={students}
          paginationState={paginationState}
          setPaginationState={handlePaginationChange}
          pageCount={Math.ceil(totalPages / perPage)}
          initialSortState={initialSortState}
          enableColumnPinning={true}
          initialState={{
            columnPinning: {
              left: ["name"],
              right: ["actions"],
            },
          }}
          manualPagination
        />
      </div>
    </div>
  );
};
